// src/components/Modal/ImageGalleryModal.jsx
import { useEffect, useMemo, useState, useCallback } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  Center,
  Box,
  Text,
  Image as ChakraImage,
  IconButton,
  HStack,
} from "@chakra-ui/react";
import { ArrowBackIcon, ArrowForwardIcon } from "@chakra-ui/icons";
import PropTypes from "prop-types";
import { AvailableIcons } from "../Icons/AvailableIcons";

/**
 * Reusable, dark-themed image gallery modal
 * - Accepts string[] (URLs) or { src, alt }[]
 * - Shows selectable thumbnails
 * - Keyboard nav (← →)
 * - Optional headerContent for custom header (e.g., receipt info)
 */

export const ImageGalleryModal = ({
  isModalOpen,
  onModalClose,
  images = [],
  initialIndex = 0,
  title = "Image Gallery",
  headerContent = null,
}) => {
  const normalized = useMemo(
    () => (images || []).map((img) => (typeof img === "string" ? { src: img, alt: "" } : img)),
    [images]
  );

  const [currentIndex, setCurrentIndex] = useState(
    Math.min(initialIndex, Math.max(0, normalized.length - 1))
  );

  useEffect(() => {
    setCurrentIndex(Math.min(initialIndex, Math.max(0, normalized.length - 1)));
  }, [initialIndex, normalized.length]);

  const hasImages = normalized.length > 0;

  const handlePrevImage = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? normalized.length - 1 : prev - 1));
  }, [normalized.length]);

  const handleNextImage = useCallback(() => {
    setCurrentIndex((prev) => (prev === normalized.length - 1 ? 0 : prev + 1));
  }, [normalized.length]);

  const handleThumbnailClick = (index) => setCurrentIndex(index);

  useEffect(() => {
    if (!isModalOpen) return;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") handlePrevImage();
      if (e.key === "ArrowRight") handleNextImage();
      if (e.key === "Escape") onModalClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isModalOpen, handlePrevImage, handleNextImage, onModalClose]);

  return (
    <Modal isOpen={isModalOpen} onClose={onModalClose} isCentered>
      <ModalOverlay />
      <ModalContent
        bg="black"
        position="relative"
        w="80vw"
        h="80vh"
        maxW="80vw"
        maxH="80vh"
        m="auto"
      >
        {/* Custom close button */}
        <Box position="absolute" top="25px" right="25px" zIndex={2}>
          <IconButton
            variant="iconOnlyTertiary"
            icon={<AvailableIcons boxSize="26px" iconRef="close" />}
            onClick={onModalClose}
            aria-label="Close"
          />
        </Box>

        {/* Use the whole canvas as a column: header/img area + thumbnails */}
        <ModalBody p="25px" pt="50px" h="100%">
          <Box
            display="flex"
            flexDirection="column"
            alignItems="stretch"
            justifyContent="space-between"
            bg="black"
            borderRadius="md"
            w="100%"
            h="100%"   // 🔥 fill the canvas
            p="20px"
            position="relative"
          >
            {/* Top: header + image stage (flex:1) */}
            <Box flex="1" display="flex" flexDirection="column" minH={0}>
              {/* Header */}
              {hasImages ? (
                headerContent ? (
                  <Box w="100%" mb="10px">{headerContent}</Box>
                ) : (
                  <Box
                    display="flex"
                    flexDirection="row"
                    alignItems="center"
                    justifyContent="space-between"
                    w="100%"
                    mb="10px"
                  >
                    <Text as="span" textStyle="heading-3" color="white">
                      {title}
                    </Text>
                    <Text as="span" textStyle="heading-3" color="white" opacity={0.7}>
                      {currentIndex + 1} / {normalized.length}
                    </Text>
                  </Box>
                )
              ) : null}

              {/* Image stage */}
              <Box
                position="relative"
                flex="1"
                display="flex"
                alignItems="center"
                justifyContent="center"
                minH={0}
                overflow="hidden"
              >
                {/* Left navigation */}
                {hasImages && (
                  <IconButton
                    icon={<ArrowBackIcon />}
                    onClick={handlePrevImage}
                    position="absolute"
                    left="5px"
                    top="50%"
                    transform="translateY(-50%)"
                    zIndex={1}
                    aria-label="Previous image"
                    variant="ghost"
                  />
                )}

                {/* Image */}
                {hasImages ? (
                  <ChakraImage
                    src={normalized[currentIndex]?.src}
                    alt={normalized[currentIndex]?.alt || "Image"}
                    maxW="100%"
                    maxH="100%"        // 🔥 never exceed stage height
                    objectFit="contain"
                    m="auto"
                    pt="4px"
                  />
                ) : (
                  <Center py={16} w="100%">
                    <Text color="whiteAlpha.700">No images to display.</Text>
                  </Center>
                )}

                {/* Right navigation */}
                {hasImages && (
                  <IconButton
                    icon={<ArrowForwardIcon />}
                    onClick={handleNextImage}
                    position="absolute"
                    right="5px"
                    top="50%"
                    transform="translateY(-50%)"
                    zIndex={1}
                    aria-label="Next image"
                    variant="ghost"
                  />
                )}
              </Box>
            </Box>

            {/* Bottom: thumbnails (always inside canvas) */}
            {hasImages && (
              <Box pt="12px" borderTop="1px solid rgba(255,255,255,0.2)">
                <Center w="100%">
                  <HStack spacing="10px" overflowX="auto" py="10px">
                    {normalized.map((thumb, index) => (
                      <Box
                        key={`${thumb.src}-${index}`}
                        border={index === currentIndex ? "2px solid white" : "2px solid transparent"}
                        borderRadius="md"
                        cursor="pointer"
                        onClick={() => handleThumbnailClick(index)}
                      >
                        <ChakraImage
                          src={thumb.src}
                          alt={`Thumbnail ${index + 1}`}
                          boxSize="50px"
                          objectFit="cover"
                          borderRadius="md"
                        />
                      </Box>
                    ))}
                  </HStack>
                </Center>
              </Box>
            )}
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

ImageGalleryModal.propTypes = {
  isModalOpen: PropTypes.bool.isRequired,
  onModalClose: PropTypes.func.isRequired,
  images: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({ src: PropTypes.string.isRequired, alt: PropTypes.string }),
    ])
  ),
  initialIndex: PropTypes.number,
  title: PropTypes.string,
  headerContent: PropTypes.node,
};